import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import {
  Ban,
  CircleAlert,
  CircleArrowRight,
  Clock3,
  FileText,
  Loader2,
  Pause,
  TriangleAlert,
  type LucideIcon,
} from 'lucide-react';

import {
  factoryService,
  factoryStatusLabel,
  type FactoryBatch,
} from '../../services/factoryService';
import type { FactoryDeskOutletContext } from '../deskOutlet';
import { useFactoryOrderScope } from '../orderScope';
import { FACTORY_WAREHOUSE_STATUS_LABEL } from '../utils/ttqProductCriticalView';

const authLabel: Record<string, string> = {
  none: '未请求授权',
  awaiting_owner_auth: '待 Owner 授权',
  factory: '已授权工厂',
  owner_self: 'Owner 自处理',
  rejected: '已拒绝',
};

/** List label: pipeline-ready legacy rows often still have warehouse_status=none. */
function warehouseListLabel(b: FactoryBatch): string | null {
  const raw = String(b.warehouseStatus || '').trim() || 'none';
  const effective = raw === 'none' && b.status === 'ready' ? 'ready' : raw;
  if (effective === 'none') return null;
  return FACTORY_WAREHOUSE_STATUS_LABEL[effective] || effective;
}

function extractFailedCount(b: FactoryBatch): number {
  const n = Number(b.meta?.extractFailed ?? 0);
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
}

const STALL_MS = 2 * 60 * 1000;

function isBatchInFlight(b: FactoryBatch): boolean {
  const st = b.phaseStatus || '';
  if (st === 'queued' || st === 'running') return true;
  return b.status === 'processing' || b.status === 'queued' || b.status === 'running';
}

type BatchStatusVisual = {
  Icon: LucideIcon;
  spin?: boolean;
  label: string;
  className: string;
  title: string;
};

function batchNeedsManual(b: FactoryBatch): boolean {
  if (b.status === 'failed') return true;
  if (b.meta?.ipttqBankBackpressureExhausted === true) return true;
  const bp = b.meta?.pipelineBackpressure;
  if (bp && typeof bp === 'object' && !Array.isArray(bp)) {
    return Object.values(bp as Record<string, { needsManual?: boolean }>).some(
      (row) => row?.needsManual === true,
    );
  }
  return false;
}

function batchStatusVisual(b: FactoryBatch): BatchStatusVisual {
  if (b.pipelinePaused) {
    const draining = b.phaseStatus === 'running' || b.status === 'processing';
    return {
      Icon: Pause,
      label: draining ? '暂停中' : '已暂停',
      className: 'text-amber-200/90',
      title: draining ? '在途调用结束后不再开新并发' : '已暂停，待恢复后继续',
    };
  }

  if (batchNeedsManual(b) && b.phaseStatus !== 'running' && b.phaseStatus !== 'queued') {
    return {
      Icon: CircleAlert,
      label: '失败·需手动再试',
      className: 'text-rose-300',
      title: b.errorMessage || '工序未达标，需从检查点继续',
    };
  }

  const status = String(b.status || '');
  if (status === 'processing' || status === 'uploading' || b.phaseStatus === 'running') {
    return {
      Icon: Loader2,
      spin: true,
      label: factoryStatusLabel[status] || '处理中',
      className: 'text-primary',
      title: '工序进行中',
    };
  }
  if (status === 'queued') {
    return {
      Icon: Clock3,
      label: '排队中',
      className: 'text-primary',
      title: '已入队，等待开工',
    };
  }
  if (status === 'ready') {
    return {
      Icon: CircleArrowRight,
      label: '就绪',
      className: 'text-emerald-300/90',
      title: '工序完成，可进入成品释放',
    };
  }
  if (status === 'layout_done') {
    return {
      Icon: CircleArrowRight,
      label: '版面就绪',
      className: 'text-emerald-300/90',
      title: '版面完成，可进入后续工序',
    };
  }
  if (status === 'failed') {
    return {
      Icon: CircleAlert,
      label: '失败',
      className: 'text-amber-300',
      title: '工序失败',
    };
  }
  if (status === 'cancelled') {
    return {
      Icon: Ban,
      label: '已取消',
      className: 'text-muted',
      title: '已取消',
    };
  }
  if (status === 'draft') {
    return {
      Icon: FileText,
      label: '草稿',
      className: 'text-muted',
      title: '尚未开工',
    };
  }
  return {
    Icon: FileText,
    label: factoryStatusLabel[status] || status || '未知',
    className: 'text-muted',
    title: status || '未知状态',
  };
}

function formatClock(ts: number): string {
  const d = new Date(ts);
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  const ss = String(d.getSeconds()).padStart(2, '0');
  return `${hh}:${mm}:${ss}`;
}

/** Match TaskDetailPage stall hint: Job idle ≥2min → amber warning. */
function jobStallLabel(jobUpdatedAt: string | null | undefined, nowMs: number): string | null {
  const t = Date.parse(String(jobUpdatedAt || ''));
  if (!Number.isFinite(t)) return null;
  const sec = Math.max(0, Math.floor((nowMs - t) / 1000));
  const clock = formatClock(t);
  if (sec >= 120) {
    return `工序 ${clock}（已 ${Math.floor(sec / 60)} 分钟无推进）`;
  }
  return null;
}

/** e.g. extract "17/110页" — ignore group counters like ttq bank 0/13. */
function extractPageProgress(label: string | null | undefined): string | null {
  if (!label) return null;
  const raw = String(label);
  // Prefer explicit 页 markers.
  const withPage = raw.match(/(\d+)\s*\/\s*(\d+)\s*页/);
  if (withPage) return `${withPage[1]}/${withPage[2]}页`;
  // extract_live / extract_page / raster style labels.
  if (!/(extract_live|extract_page|page_direct_raster|页)/i.test(raw)) return null;
  const m = raw.match(/(\d+)\s*\/\s*(\d+)/);
  if (!m) return null;
  return `${m[1]}/${m[2]}页`;
}

function extractGroupProgress(label: string | null | undefined): string | null {
  if (!label) return null;
  const raw = String(label);
  if (!/(bank_stock|product_stock|product_resolve|title_condense|ipttq_condense)/i.test(raw)) {
    return null;
  }
  const m = raw.match(/(\d+)\s*\/\s*(\d+)/);
  if (!m) return null;
  return `${m[1]}/${m[2]}组`;
}

function batchProgressSuffix(b: FactoryBatch): string {
  const parts: string[] = [];
  const phaseLabel = b.currentPhaseLabel || null;
  const showPhase =
    Boolean(phaseLabel)
    && (isBatchInFlight(b) || b.phaseStatus === 'failed' || b.status === 'failed');
  if (showPhase && phaseLabel) {
    const pct =
      b.progressPct != null && Number.isFinite(Number(b.progressPct))
        ? ` ${Math.round(Number(b.progressPct))}%`
        : '';
    parts.push(`${phaseLabel}${pct}`);
  }
  const pageBit = extractPageProgress(b.progressLabel);
  const groupBit = extractGroupProgress(b.progressLabel);
  if (pageBit && showPhase) parts.push(pageBit);
  else if (groupBit && showPhase) parts.push(groupBit);
  return parts.length ? ` · ${parts.join(' · ')}` : '';
}

export function FactoryTasksPage() {
  const { refreshKey = 0 } = useOutletContext<FactoryDeskOutletContext>() ?? { refreshKey: 0 };
  const { activeOrderId } = useFactoryOrderScope();
  const [batches, setBatches] = useState<FactoryBatch[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [now, setNow] = useState(() => Date.now());

  const reload = useCallback(async () => {
    setBatches(await factoryService.listBatches());
  }, []);

  const scopedBatches = useMemo(
    () => (activeOrderId ? batches.filter((b) => b.orderId === activeOrderId) : []),
    [activeOrderId, batches],
  );
  const anyInFlight = useMemo(() => scopedBatches.some(isBatchInFlight), [scopedBatches]);
  const anyPossiblyStalled = useMemo(
    () => scopedBatches.some((b) => {
      if (!isBatchInFlight(b) || !b.jobUpdatedAt) return false;
      const t = Date.parse(b.jobUpdatedAt);
      return Number.isFinite(t) && Date.now() - t >= STALL_MS;
    }),
    [scopedBatches],
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setError(null);
        if (refreshKey > 0) setLoading(true);
        await reload();
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [reload, refreshKey]);

  useEffect(() => {
    if (!anyInFlight) return;
    // Poll AppFactory /batches only (never FC). 4–8s while work is in flight.
    const timer = window.setInterval(() => {
      void reload().catch(() => undefined);
    }, 6000);
    return () => window.clearInterval(timer);
  }, [anyInFlight, reload]);

  useEffect(() => {
    if (!anyInFlight && !anyPossiblyStalled) return;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [anyInFlight, anyPossiblyStalled]);

  return (
    <div className="space-y-5">
      <header>
        <h1 className="text-xl font-semibold text-ink">任务</h1>
        <p className="mt-1 text-sm text-muted">
          当前订单下的进厂任务。点进可看顺序工序 Phase（版面拆分 → 结构聚合 → 颗粒提取 → 插图提取 → 品题推荐）。
          右侧金额为从宽估算（Qwen3.7-Plus token + FC CU ×1.2），非账单实扣。切换订单请回「订单」。
        </p>
      </header>
      {error ? <p className="text-sm text-danger">{error}</p> : null}
      <section className="rounded-2xl border border-border bg-surface p-4 shadow-sm">
        {loading ? <p className="text-sm text-muted">加载中…</p> : null}
        {!loading && scopedBatches.length === 0 ? (
          <p className="text-sm text-muted">
            {activeOrderId
              ? '当前订单暂无任务。请先在「源货架」上架并进厂。'
              : '请先在「订单」选择当前订单。'}
          </p>
        ) : null}
        <ul className="divide-y divide-border">
          {scopedBatches.map((b) => {
            const stall = isBatchInFlight(b) ? jobStallLabel(b.jobUpdatedAt, now) : null;
            const warehouseLabel = warehouseListLabel(b);
            const failN = extractFailedCount(b);
            const statusVisual = batchStatusVisual(b);
            const StatusIcon = statusVisual.Icon;
            const costLabel = String(b.costEstimateLabel || '').trim();
            const costTitle = b.costEstimate
              ? [
                  `Token ≈ ¥${b.costEstimate.tokenCny}`,
                  `FC ≈ ¥${b.costEstimate.fcCny}（估 ${Number(b.costEstimate.estimatedFcCu || 0).toLocaleString('zh-CN')} CU）`,
                  `裕度 ×${b.costEstimate.margin}`,
                  b.costEstimate.incomplete ? 'token 明细尚不完整' : '',
                ].filter(Boolean).join(' · ')
              : '成本估算';
            return (
              <li key={b.id} className="py-3 first:pt-0 last:pb-0">
                <div className="flex items-start justify-between gap-3">
                  <Link to={`/factory/tasks/${b.id}`} className="min-w-0 flex-1 block hover:text-primary">
                    <div className="font-medium text-ink">{b.title || '未命名任务'}</div>
                    <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted">
                      <span
                        className="inline-flex items-center gap-1"
                        title={statusVisual.title}
                        aria-label={statusVisual.title}
                      >
                        <StatusIcon
                          size={12}
                          strokeWidth={2.25}
                          className={`${statusVisual.className}${statusVisual.spin ? ' animate-spin' : ''}`}
                          aria-hidden
                        />
                        <span>
                          <span className={statusVisual.className}>{statusVisual.label}</span>
                          {batchProgressSuffix(b)}
                        </span>
                      </span>
                      {stall ? (
                        <span
                          className="font-medium text-amber-200/90"
                          title="服务端 Job 最近 progress 更新时间"
                        >
                          {stall}
                        </span>
                      ) : null}
                      <span>{authLabel[b.processingAuthorization || ''] || b.processingAuthorization || '—'}</span>
                      {warehouseLabel ? <span>库存：{warehouseLabel}</span> : null}
                    </div>
                  </Link>
                  <div className="flex shrink-0 flex-col items-end gap-1.5">
                    {failN > 0 ? (
                      <div className="flex items-center gap-1.5">
                        <span
                          className="inline-flex items-center gap-1 rounded-md border border-amber-500/45 bg-amber-500/10 px-1.5 py-0.5 text-[11px] font-semibold text-amber-300"
                          title={`有 ${failN} 页知识颗粒提取失败`}
                        >
                          <TriangleAlert size={12} strokeWidth={2.25} aria-hidden />
                          提取失败 {failN}
                        </span>
                        <Link
                          to={`/factory/tasks/${b.id}/results?phase=extract&warn=extract`}
                          className="rounded-md border border-amber-500/45 bg-amber-500/10 px-2 py-1 text-[11px] font-semibold text-amber-300 hover:bg-amber-500/20"
                          title="打开颗粒提取结果并查看失败单元"
                        >
                          处理
                        </Link>
                      </div>
                    ) : null}
                    {costLabel ? (
                      <span
                        className="text-xs font-semibold tabular-nums text-emerald-300/90"
                        title={costTitle}
                      >
                        {costLabel}
                      </span>
                    ) : null}
                    <span className="text-xs tabular-nums text-muted">
                      {new Date(b.createdAt).toLocaleString()}
                    </span>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      </section>
    </div>
  );
}
